import { useEffect, useRef } from "react";

const trailLength = 10;
const fadeMs = 700;

type TrailPoint = {
  born: number;
  x: number;
  y: number;
};

export function InkTrail() {
  const trailRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      window.matchMedia("(hover: none)").matches ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const nodes = Array.from(
      trailRef.current?.children ?? [],
    ) as HTMLSpanElement[];
    if (!nodes.length) return;

    const points: TrailPoint[] = Array.from({ length: trailLength }, () => ({
      born: 0,
      x: -80,
      y: -80,
    }));
    let index = 0;
    let frame = 0;
    let lastStamp = 0;
    let cursorX = -80;
    let cursorY = -80;

    function handleMove(event: MouseEvent) {
      cursorX = event.clientX;
      cursorY = event.clientY;
      const now = performance.now();
      if (now - lastStamp < 42) return;
      lastStamp = now;
      points[index] = { born: now, x: cursorX, y: cursorY };
      index = (index + 1) % trailLength;
    }

    function animate(now: number) {
      points.forEach((point, pointIndex) => {
        const age = now - point.born;
        const opacity = point.born ? Math.max(0, 0.7 * (1 - age / fadeMs)) : 0;
        const lag = pointIndex / trailLength;
        const driftX = (cursorX - point.x) * 0.025 * lag;
        const driftY = (cursorY - point.y) * 0.025 * lag;
        const node = nodes[pointIndex];
        node.style.opacity = opacity.toFixed(3);
        node.style.transform = `translate3d(${point.x + driftX - 3}px, ${point.y + driftY - 3}px, 0) scale(${0.78 + opacity * 0.45})`;
      });
      frame = window.requestAnimationFrame(animate);
    }

    window.addEventListener("mousemove", handleMove, { passive: true });
    frame = window.requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div ref={trailRef} className="ink-trail" aria-hidden="true">
      {Array.from({ length: trailLength }, (_, index) => (
        <span key={index} />
      ))}
    </div>
  );
}
